import { amorphicStatic } from '@haventech/amorphic';
import * as ld from 'launchdarkly-node-server-sdk';
import { LDClient, LDContext, LDContextCommon } from 'launchdarkly-node-server-sdk';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

/**
 * Usage - Pass in the "app" to getInstance function to get a singleton LaunchDarklyInstance
 * for that app.
 */
export class LaunchDarklyInstance {
  private static _instances: { [key: string]: LaunchDarklyInstance } = {};
  private ldClient: LDClient;
  private static moduleName: string = 'LaunchDarklyInstance';

  private constructor() {}

  static async getInstance(app: LD_APP): Promise<LaunchDarklyInstance | undefined> {
    const functionName: string = 'getInstance';
    try {
      if (!this._instances[app]) {
        const ldKey = amorphicStatic.config.nconf.get(`${app}-launchDarklyKey`);
        if (ldKey) {
          const ldInstance = new LaunchDarklyInstance();
          this._instances[app] = await this.initialize(ldInstance, ldKey);
        }
      }
      return this._instances[app];
    } catch (e) {
      this.logError(e, functionName, 'instantiation');
    }
    return undefined;
  }

  private static async initialize(ldInstance: LaunchDarklyInstance, ldKey: string): Promise<LaunchDarklyInstance> {
    const offline = amorphicStatic.config.nconf.get('launchDarklyOfflineMode');
    ldInstance.ldClient = ld.init(ldKey, {
      allAttributesPrivate: true,
      offline: !!offline,
      logger: UILoggerSingleton.getInstance(),
    });

    await ldInstance.ldClient.waitForInitialization();
    return ldInstance;
  }

  async getFeatureFlagJSONValue<ResponseType>(flag: string, userId: string): Promise<ResponseType> {
    const functionName: string = 'getFeatureFlagJSONValue';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsContext(userId), {});
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsArray<T>(flag: string, userId: string): Promise<T[]> {
    const functionName: string = 'getFeatureFlagValueAsArray';
    try {
      const flagValue = await this.ldClient.variation(flag, this.buildLaunchDarklyParamsContext(userId), []);
      return flagValue as T[];
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsBooleanWithId(flag: string, userId: string): Promise<boolean> {
    const functionName: string = 'getFeatureFlagValueAsBooleanWithId';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsContext(userId), false);
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsBoolean(flag: string, userId: string): Promise<boolean> {
    const functionName: string = 'getFeatureFlagValueAsBoolean';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsContext(userId), false);
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsString(flag: string, userId: string, defaultValue = ''): Promise<string> {
    const functionName: string = 'getFeatureFlagValueAsString';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsContext(userId), defaultValue);
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsNumber(flag: string, userId: string, defaultValue: number): Promise<number> {
    const functionName: string = 'getFeatureFlagValueAsNumber';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsContext(userId), defaultValue);
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsJSON<JSON_TYPE>(
    flag: string,
    userId: string,
    defaultValue: JSON_TYPE
  ): Promise<JSON_TYPE> {
    const functionName: string = 'getFeatureFlagValueAsJSON';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsContext(userId), defaultValue);
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsBooleanWithIDAndCustom(
    flag: string,
    userId: string,
    customParams: { [key: string]: string | string[] | number }
  ): Promise<boolean> {
    const functionName: string = 'getFeatureFlagValueAsBooleanWithIDAndCustom';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsCustom(userId, customParams), false);
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  async getFeatureFlagValueAsStringWithIDAndCustom(
    flag: string,
    userId: string,
    customParams: { [key: string]: string | string[] | number }
  ): Promise<string> {
    const functionName: string = 'getFeatureFlagValueAsStringWithIDAndCustom';
    try {
      return await this.ldClient.variation(flag, this.buildLaunchDarklyParamsCustom(userId, customParams), null);
    } catch (error) {
      LaunchDarklyInstance.logError(error, functionName, flag);
    }
  }

  // private logInfo(functionName: string, flag: string, flagValue) {
  //   if (Utils.isTestingEnv()) {
  //     UILoggerSingleton.getInstance().info(
  //       {
  //         module: LaunchDarklyInstance.moduleName,
  //         function: functionName,
  //         UICategory: UICategory.availability,
  //
  //         data: {
  //           flag: flag,
  //           flagValue: flagValue,
  //         },
  //       },
  //       'Launch Darkly flag evaluated'
  //     );
  //   }
  // }

  private static logError(error, functionName: string, flag: string) {
    UILoggerSingleton.getInstance().error({
      module: this.moduleName,
      function: functionName,
      category: UICategory.request,
      flag: flag,
      error: {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        isHumanRelated: false,
      },
    });
  }

  private buildLaunchDarklyParamsCustom(
    userId: string,
    custom: { [key: string]: string | string[] | number }
  ): LDContext {
    const context = this.buildLaunchDarklyParamsContext(userId);
    return {
      ...context,
      ...custom,
    };
  }

  private buildLaunchDarklyParamsContext(userId: string): LDContextCommon {
    return {
      kind: 'user',
      key: userId,
    };
  }
}

export enum LD_APP {
  cp = 'cp',
}
